/* @flow */

import type { ContextRouter } from 'react-router-dom';
import * as React from 'react';

import { MediaTypeProvider } from '@sonyged/ext-proc';

import { type FinishedRound, type FutureRound, History, type NewRound, Round } from './domain/Round';
import { Rng } from '../../../utils/Random';
import { impossible } from './domain/utils';
import AppContext from '../../../AppContext';
import CompletionPage from '../../common/CompletionPage';
import Menu from '../../ice/fields/Menu';
import PizzaBakingView from './PizzaBakingView';
import PizzaBuilderView from './PizzaBuilderView';
import PizzaCookingView from './PizzaCookingView';
import PizzaEatingView from './PizzaEatingView';
import PizzaOrderView from './PizzaOrderView';
import PizzaResultView from './PizzaResultView';
import PizzaStatsView from './PizzaStatsView';

type Props = { ...ContextRouter };

type Stage =
  | {
      stage: 'order' | 'build',
      round: NewRound,
    }
  | {
      stage: 'stats',
      round?: NewRound,
    }
  | {
      stage: 'result' | 'resultFromStats',
      round: FinishedRound,
      pendingRound?: NewRound,
    }
  | {
      stage: 'cook' | 'bake' | 'eat',
      round: FinishedRound,
    }
  | {
      stage: 'end',
    };

type States = {
  history: FinishedRound[],
  current: Stage,
  futureRounds: FutureRound[],
};

class PizzaFieldChallengePage extends React.Component<Props, States> {
  static contextType = AppContext;

  rng: Rng;

  constructor(PizzaFieldProps: Props) {
    super(PizzaFieldProps);

    this.rng = new Rng(Math.random());

    this.state = {
      history: [],
      current: { stage: 'stats', round: Round.create('1くみ目', 0, this.rng) },
      futureRounds: [2, 3, 4, 5].map(i => ({ name: `${i}くみ目` })),
    };

    (this: any).switchToPizzaForm = this.switchToPizzaForm.bind(this);
    (this: any).switchToCooking = this.switchToCooking.bind(this);
    (this: any).switchToBaking = this.switchToBaking.bind(this);
    (this: any).switchToEating = this.switchToEating.bind(this);
    (this: any).finishRound = this.finishRound.bind(this);
    (this: any).resultToStats = this.resultToStats.bind(this);
    (this: any).statsToResult = this.statsToResult.bind(this);

    (this: any).handleStatsOnNext = this.handleStatsOnNext.bind(this);
  }

  componentDidMount() {
    this.context.soundController.bgmPlay('proc_field03_pizza', true);
  }

  switchToPizzaForm(round: NewRound) {
    this.setState({ current: { round, stage: 'build' } });
  }

  switchToCooking(round: FinishedRound) {
    this.setState({ current: { round, stage: 'cook' } });
  }

  switchToBaking(round: FinishedRound) {
    this.setState({ current: { round, stage: 'bake' } });
  }

  switchToEating(round: FinishedRound) {
    this.setState({ current: { round, stage: 'eat' } });
  }

  finishRound(round: FinishedRound) {
    const { history, futureRounds } = this.state;
    const newHistory = history.concat(round);
    if (futureRounds.length) {
      this.setState({
        current: {
          round,
          stage: 'result',
          pendingRound: Round.create(futureRounds[0].name, History.totalRating(newHistory), this.rng),
        },
        futureRounds: futureRounds.slice(1),
        history: newHistory,
      });
    } else {
      this.setState({
        current: {
          round,
          stage: 'result',
        },
        futureRounds: futureRounds.slice(1),
        history: newHistory,
      });
    }
  }

  resultToStats() {
    const current = this.state.current;
    if (current.stage !== 'result' && current.stage !== 'resultFromStats') return;

    this.setState({
      current: {
        stage: 'stats',
        round: current.pendingRound,
      },
    });
  }

  statsToResult(round: FinishedRound) {
    const current = this.state.current;
    if (current.stage !== 'stats') return;

    this.setState({
      current: {
        round,
        stage: 'resultFromStats',
        pendingRound: current.round,
      },
    });
  }

  handleStatsOnNext() {
    if (this.state.current.stage !== 'stats') return;
    const currentRound = this.state.current.round;
    if (currentRound) {
      this.setState({
        current: {
          stage: 'order',
          round: currentRound,
        },
      });
    } else {
      this.setState({ current: { stage: 'end' } });
    }
  }

  currentPage() {
    const { current } = this.state;
    switch (current.stage) {
      case 'order':
        return <PizzaOrderView round={current.round} onNext={this.switchToPizzaForm} />;
      case 'build':
        return (
          <PizzaBuilderView
            round={current.round}
            onNext={this.switchToCooking}
            onBack={() => {
              this.setState({ current: { round: current.round, stage: 'order' } });
            }}
          />
        );
      case 'cook':
        return <PizzaCookingView round={current.round} onNext={this.switchToBaking} />;
      case 'bake':
        return <PizzaBakingView round={current.round} onNext={this.switchToEating} />;
      case 'eat':
        return <PizzaEatingView round={current.round} onNext={this.finishRound} />;
      case 'result':
        return <PizzaResultView round={current.round} onNext={this.resultToStats} />;
      case 'resultFromStats':
        return <PizzaResultView fromStats={true} round={current.round} onNext={this.resultToStats} />;
      case 'stats':
        return (
          <PizzaStatsView
            current={current.round}
            future={this.state.futureRounds}
            history={this.state.history}
            showResult={this.statsToResult}
            onNext={this.handleStatsOnNext}
          />
        );
      case 'end':
        return (
          <CompletionPage
            history={this.props.history}
            kind={'pizza'}
            totalScore={History.totalSale(this.state.history)}
          />
        );
      default:
        return impossible(current.stage);
    }
  }

  render() {
    const showMenu = ['cook', 'bake'].every(p => p !== this.state.current.stage);
    let menuText = '';

    switch (this.state.current.stage) {
      case 'order':
      case 'build':
      case 'cook':
      case 'bake':
      case 'eat':
        menuText = this.state.current.round.name;
        break;
      case 'result':
      case 'resultFromStats':
        menuText = 'けっか ' + this.state.current.round.name;
        break;
      case 'stats':
        menuText = 'いちらん';
        break;
      case 'end':
        break;
      default:
        console.error(`unknown ${this.state.current.stage}`);
    }

    return (
      <MediaTypeProvider>
        <div id="proc-pizza-fields-root-page" className="pizza proc-pizza-field-challenge">
          {showMenu && (
            <Menu
              exitLink={'/pizza/fields/'}
              query={'?skip_intro=true'}
              menuText={menuText}
              title={''}
              subTitle={'おすすめの ピザを つくろう'}
              rootNodeId="proc-pizza-fields-root-page"
            />
          )}
          {this.currentPage()}
        </div>
      </MediaTypeProvider>
    );
  }
}

export default PizzaFieldChallengePage;
